import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AssignmentFileLink } from "@/components/AssignmentFileLink";
import { StudentTurnInPanel } from "@/components/StudentTurnInPanel";

export default function AssignmentDetail() {
  const { assignmentId } = useParams();
  const { user } = useAuth();
  const [assignment, setAssignment] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/assignments/${assignmentId}`);
      setAssignment(data); setError("");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Assignment unavailable");
    }
  }, [assignmentId]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = useCallback((submissionStatus) => {
    const status = { not_started: "assigned", draft: "in_progress" }[submissionStatus] || submissionStatus;
    setAssignment((current) => current ? ({ ...current, status }) : current);
  }, []);

  if (error) return <div data-testid="assignment-detail-error" className="p-6 rounded-2xl bg-red-50 border border-red-200 text-red-800">{error}</div>;
  if (!assignment) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid={`assignment-detail-${assignment.id}`}>
      <Link to={user?.role === "parent" ? `/parent/student/${assignment.student_id}` : "/student"}
        data-testid="assignment-detail-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700">
        <ArrowLeft size={14} /> Back
      </Link>
      <header>
        <div className="chip mb-2">Assignment · {assignment.subject}</div>
        <h1 data-testid="assignment-detail-title" className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{assignment.title}</h1>
        <div className="mt-2 text-sm text-slate-500">Due {assignment.due_date || "not set"} · {assignment.status}</div>
      </header>

      <section className="p-6 rounded-2xl bg-white border border-slate-200" data-testid="assignment-instructions">
        <h2 className="font-display text-lg font-semibold">Instructions</h2>
        <div className="mt-2 whitespace-pre-wrap text-slate-700">{assignment.description || "No additional instructions."}</div>
        {assignment.video_url && <a data-testid="assignment-detail-video" href={assignment.video_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-teal-700 font-semibold">Open video link ↗</a>}
        {assignment.attachments?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-3" data-testid="assignment-detail-attachments">
            {assignment.attachments.map((file, index) => <AssignmentFileLink key={file.file_id || index} file={file} testId={`assignment-detail-file-${index}`} />)}
          </div>
        )}
      </section>

      {user?.role === "student" && <StudentTurnInPanel assignment={assignment} onStatusChange={updateStatus} />}

      {user?.role === "parent" && (assignment.answer_key_text || assignment.answer_key_file) && (
        <section className="p-6 rounded-2xl bg-amber-50 border border-amber-200" data-testid="assignment-detail-answer-key">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><KeyRound size={17} /> Parent Answer Key</h2>
          {assignment.answer_key_text && <div className="mt-2 whitespace-pre-wrap text-slate-800">{assignment.answer_key_text}</div>}
          {assignment.answer_key_file && <div className="mt-3"><AssignmentFileLink file={assignment.answer_key_file} testId="assignment-detail-answer-key-file" /></div>}
        </section>
      )}

    </div>
  );
}